// All-chores view — every chore in the household, both kid and adult,
// in one normalized list. Server returns them via the
// chores.allForHousehold RPC; the client groups by frequency and
// offers filter chips per member.
//
// Each row dispatches mark-done / delete based on its `kind`:
//   - kid → choresToggleDone / choresArchive (soft-archive)
//   - adult → adultChoresMarkDone / adultChoresDelete (hard delete)
//
// Visual: same row shape for both. Adult chores show their hue
// stripe; kid chores show the icon emoji where the stripe would be.
// Done-today rows fade and strike through.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import {
  api,
  type HouseholdMember,
  type UnifiedChore,
  type UnifiedChoreFrequency,
} from "../../lib/rpc";
import { confirm } from "../../components/confirm";

interface AllChoresProps {
  members: HouseholdMember[];
  notify: (msg: string) => void;
  /** Bumped by the parent after any mutation that affects either
   *  chart so the All view reloads alongside Today. */
  refreshToken: number;
  /** Fires when the All view mutates so the parent (which also
   *  shows Today) can refresh its own state. */
  onMutated: () => void;
  /** Open the edit sheet on this chore — handled by the parent so it
   *  can route to the shared NewChoreSheet with the right edit prop
   *  (adult vs kid). */
  onEdit: (chore: UnifiedChore) => void;
}

const FREQ_ORDER: UnifiedChoreFrequency[] = [
  "daily", "weekly", "biweekly", "monthly", "quarterly", "once", "rrule",
];
const FREQ_LABEL: Record<UnifiedChoreFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  once: "Just once",
  rrule: "Custom",
};

export function AllChores({ members, notify, refreshToken, onMutated, onEdit }: AllChoresProps) {
  const [chores, setChores] = useState<UnifiedChore[] | null>(null);
  const [today, setToday] = useState<string>("");
  const [filter, setFilter] = useState<string>("__all");

  const refresh = async () => {
    try {
      const r = await api.choresAllForHousehold();
      setChores(r.chores);
      setToday(r.today);
    } catch (e: any) {
      notify(e?.message || "couldn't load all chores");
      setChores([]);
    }
  };

  useEffect(() => { void refresh(); }, [refreshToken]);

  const onDone = async (c: UnifiedChore) => {
    try {
      if (c.kind === "kid") {
        const r = await api.choresToggleDone(c.rawId);
        if (r.action === "done") {
          notify(r.pendingApproval ? "Waiting for parent OK!" : `⭐ +${r.starsAwarded || 0}`);
        } else {
          notify("Undone");
        }
      } else {
        const r = await api.adultChoresMarkDone(c.rawId);
        notify(r.starsAwarded ? `Done · ⭐ +${r.starsAwarded}` : "Done");
      }
      await refresh();
      onMutated();
    } catch (e: any) { notify(e?.message || "couldn't update"); }
  };

  const onDelete = async (c: UnifiedChore) => {
    const ok = await confirm({
      title: `${c.kind === "kid" ? "Archive" : "Delete"} "${c.name}"?`,
      message: c.kind === "kid"
        ? "It'll stop showing up on the chart. Past completions stay."
        : "Removes the chore and its completion history.",
      confirmLabel: c.kind === "kid" ? "Archive" : "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      if (c.kind === "kid") await api.choresArchive(c.rawId);
      else await api.adultChoresDelete(c.rawId);
      notify(c.kind === "kid" ? "Archived" : "Deleted");
      await refresh();
      onMutated();
    } catch (e: any) { notify(e?.message || "couldn't remove"); }
  };

  // Filter chips: "Everyone" + one chip per household member. The
  // chip count uses the unified list's current set so it tracks
  // mark-done state (a chore goes from "yours this week" to "done"
  // but stays in the column count — it's the same chore, just
  // visually dimmed). Adults first, then kids — adults are the
  // typical owners of the All view (kids manage from KidApp).
  const orderedMembers = useMemo(
    () => [
      ...members.filter((m) => m.role !== "kid").sort((a, b) => a.userId.localeCompare(b.userId)),
      ...members.filter((m) => m.role === "kid").sort((a, b) => a.userId.localeCompare(b.userId)),
    ],
    [members],
  );

  const filtered = useMemo(() => {
    if (!chores) return [];
    if (filter === "__all") return chores;
    if (filter === "__unassigned") return chores.filter((c) => !c.assignee);
    return chores.filter((c) => c.assignee === filter);
  }, [chores, filter]);

  const groupedByFreq = useMemo(() => {
    const map = new Map<UnifiedChoreFrequency, UnifiedChore[]>();
    for (const c of filtered) {
      const arr = map.get(c.frequency) || [];
      arr.push(c);
      map.set(c.frequency, arr);
    }
    return FREQ_ORDER
      .filter((f) => (map.get(f)?.length ?? 0) > 0)
      .map((f) => ({ freq: f, items: map.get(f)! }));
  }, [filtered]);

  const memberCount = (memberId: string | null) => {
    if (!chores) return 0;
    if (memberId === null) return chores.filter((c) => !c.assignee).length;
    return chores.filter((c) => c.assignee === memberId).length;
  };

  return (
    <div className="chores-all">
      {/* Filter chips */}
      <div className="chores-all-chips">
        <FilterChip
          label={`Everyone · ${chores?.length ?? 0}`}
          active={filter === "__all"}
          onPick={() => setFilter("__all")}
        />
        {orderedMembers.map((m) => {
          const count = memberCount(m.userId);
          if (count === 0) return null;
          return (
            <FilterChip
              key={m.userId}
              label={`${m.userId}${m.role === "kid" ? " · kid" : ""} · ${count}`}
              active={filter === m.userId}
              onPick={() => setFilter(m.userId)}
            />
          );
        })}
        {memberCount(null) > 0 && (
          <FilterChip
            label={`Unassigned · ${memberCount(null)}`}
            active={filter === "__unassigned"}
            onPick={() => setFilter("__unassigned")}
          />
        )}
      </div>

      {chores === null && <div className="chores-loading">loading…</div>}

      {chores !== null && filtered.length === 0 && (
        <div className="chores-empty-card">
          <p><em>{filter === "__all" ? "No chores on the chart yet." : "Nothing here for that filter."}</em></p>
          {filter === "__all" && (
            <p className="chores-empty-sub">Tap "New chore" up top to add the first.</p>
          )}
        </div>
      )}

      {groupedByFreq.map(({ freq, items }) => (
        <section key={freq} className="chores-all-group">
          <header className="chores-all-group-head">
            <div className="eyebrow">{FREQ_LABEL[freq]}</div>
            <span className="chores-all-group-rule" />
            <span className="chores-all-group-count">{items.length}</span>
          </header>
          <div className="chores-list">
            {items.map((c) => (
              <UnifiedRow
                key={c.id}
                chore={c}
                today={today}
                onDone={() => onDone(c)}
                onDelete={() => onDelete(c)}
                onEdit={() => onEdit(c)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Filter chip ───────────────────────────────────────────────────

interface FilterChipProps {
  label: string;
  active: boolean;
  onPick: () => void;
}

function FilterChip({ label, active, onPick }: FilterChipProps) {
  return (
    <button
      type="button"
      className="chores-all-chip"
      data-active={active}
      onClick={onPick}
    >
      {label}
    </button>
  );
}

// ── Unified row (kid + adult) ────────────────────────────────────

interface UnifiedRowProps {
  chore: UnifiedChore;
  today: string;
  onDone: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

function UnifiedRow({ chore, today, onDone, onDelete, onEdit }: UnifiedRowProps) {
  const overdue = chore.kind === "adult"
    && chore.nextDue !== null
    && chore.nextDue < today
    && chore.lastDone !== today;
  const dimmed = chore.doneToday;
  return (
    <div
      className="chores-row chores-row-unified"
      data-overdue={overdue}
      data-done={dimmed}
      data-kind={chore.kind}
    >
      <button
        className="chores-check"
        type="button"
        onClick={onDone}
        title={dimmed && chore.kind === "kid" ? "Undo" : "Mark done"}
      >
        <Icon name="check" size={12} />
      </button>
      {chore.kind === "adult" && chore.hue !== null ? (
        <span className="chores-hue" style={{ background: `oklch(0.75 0.16 ${chore.hue})` }} />
      ) : (
        <span className="chores-row-icon">{chore.icon || (chore.kind === "kid" ? "✅" : "•")}</span>
      )}
      <div className="chores-row-body">
        <div className="chores-row-name">{chore.name}</div>
        <div className="chores-row-meta">
          {chore.category && (
            <>
              <span>{chore.category}</span>
              <span className="chores-row-dot">·</span>
            </>
          )}
          <span>{chore.frequency}</span>
          {chore.lastDone && (
            <>
              <span className="chores-row-dot">·</span>
              <span>last {relativeDate(chore.lastDone, today)}</span>
            </>
          )}
          {chore.requiresApproval && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-row-approval">approval</span>
            </>
          )}
          {chore.pendingApproval && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-row-pending">⏳ waiting</span>
            </>
          )}
          {chore.stars > 0 && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-row-stars">⭐ {chore.stars}</span>
            </>
          )}
        </div>
      </div>
      <div className="chores-row-owner">
        {chore.assignee ? (
          <span className="chores-row-owner-pill" data-kid={chore.kind === "kid"}>
            {chore.assignee}
          </span>
        ) : (
          <span className="chores-row-owner-pill chores-row-owner-pill-unassigned">anyone</span>
        )}
      </div>
      <UnifiedChoreMenu onEdit={onEdit} onDelete={onDelete} kidArchive={chore.kind === "kid"} />
    </div>
  );
}

// Per-row ⋯ menu — Edit + (Delete | Archive). Same UI shape as the
// adult Today menu so the All view feels consistent.
function UnifiedChoreMenu({ onEdit, onDelete, kidArchive }: { onEdit: () => void; onDelete: () => void; kidArchive: boolean }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const w = wrapRef.current;
      if (w && !w.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDocPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="chores-row-menu-wrap" ref={wrapRef}>
      <button
        className="chores-row-more"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="More"
      >
        <Icon name="more" size={14} />
      </button>
      {open && (
        <div className="chores-row-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="chores-row-menu-item"
            onClick={() => { setOpen(false); onEdit(); }}
            data-testid="unified-row-edit"
          >
            <Icon name="edit" size={12} /> Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="chores-row-menu-item chores-row-menu-danger"
            onClick={() => { setOpen(false); onDelete(); }}
            data-testid="unified-row-remove"
          >
            <Icon name="x" size={12} /> {kidArchive ? "Archive" : "Delete"}
          </button>
        </div>
      )}
    </div>
  );
}

function relativeDate(iso: string | null, todayIso: string): string {
  if (!iso) return "never";
  const target = new Date(`${iso}T00:00:00Z`);
  const today = new Date(`${todayIso}T00:00:00Z`);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  if (diff > 0 && diff <= 6) return `in ${diff}d`;
  if (diff < 0 && diff >= -6) return `${Math.abs(diff)}d ago`;
  return target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
